import {
  Card,
  Flex,
  Icon,
  Loader,
  Menu,
  Spinner,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { IAnalytic } from 'common/interfaces/analytics';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { runAnalytic, fetchCachedResult, analyticFiltersUpdated } from 'client/store/entities/analytics/analytics';
import {
  getAnalyticResult, getAnalyticRunState, getFilters, getLoadingState, getSelectedDashboardId,
} from 'client/store/entities/analytics/selectors';
import { DateService } from 'common/services/date-service';
import { AnalyticsCardContent } from './analytic-card-content';

export const AnalyticsCard = ({
  analytic, onEdit, onRemove,
}: {
  analytic: IAnalytic;
  onEdit: (item: IAnalytic) => void;
  onRemove: (item: IAnalytic) => void;
}) => {
  const itemRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [widthDimensions, setWidthDimension] = useState('0');
  const selectedDashboardId = useSelector(getSelectedDashboardId);
  const { isChangingAnalytic } = useSelector(getLoadingState);
  const analyticFilters = useSelector(state => getFilters(state, analytic.analyticid));
  const { result, query, runDate } = useSelector(state => getAnalyticResult(state, analytic.analyticid));
  const formattedRunDate = DateService.format(runDate);

  useEffect(() => {
    if (analytic.analyticid) {
      dispatch(fetchCachedResult(analytic.analyticid));
    }
  }, [analytic.analyticid]);

  const queryWithFilters = {
    ...query,
    resultConfig: {
      ...query?.resultConfig,
      filters: {
        ...query?.resultConfig?.filters,
        // @ts-ignore
        ...analyticFilters,
      },
    },
  };

  const isRunningAnalyticQuery = useSelector(state => getAnalyticRunState(state, analytic.analyticid));

  const handleRunAnalytic = () => dispatch(runAnalytic(analytic.analyticid!));

  const setAnalyticFilters = (filterValue: { [key: string]: string }) => {
    dispatch(analyticFiltersUpdated({
      dashboardid: selectedDashboardId,
      analyticid: analytic.analyticid,
      filterValue,
    }));
  };

  useLayoutEffect(() => {
    if (itemRef.current) {
      const { width } = window.getComputedStyle(itemRef.current);
      setWidthDimension(width);
    }
  });

  if (isChangingAnalytic[analytic.analyticid!]) {
    return <Loader text="updating item" width={widthDimensions} height={200} theme="light"><Spinner dark /></Loader>;
  }

  const cardTitle = `${analytic?.name} run at ${formattedRunDate}`;

  return (
    <Card
      bordered
      padding={false}
      heading={(
        <div style={{ width: '100%' }}>
          <Flex justifyContent="space-between">
            {cardTitle}
            <Menu
              menu={{
                component: <Icon
                  icon="settings"
                />,
                label: 'Select',
                sections: [
                  {
                    label: t(translations.edit),
                    onClick: () => onEdit(analytic),
                    type: 'Option',
                  },
                  {
                    label: t(translations.delete),
                    onClick: () => onRemove(analytic),
                    type: 'Option',
                  },
                  {
                    label: t(translations.analytic_run),
                    onClick: handleRunAnalytic,
                    type: 'Option',
                  },
                ],
                trigger: 'Component',
              }}
            />
          </Flex>
        </div>
      )}
    >
      <AnalyticsCardContent
        ref={itemRef}
        onRun={handleRunAnalytic}
        result={{
          result,
          query: queryWithFilters,
        }}
        isRunning={isRunningAnalyticQuery}
        onFiltersUpdate={setAnalyticFilters}
      />
    </Card>
  );
};
